import { useState, useEffect } from 'react';
import { useMasterReferenceQuery } from './useMasterReferenceGetItemListQuery';
import { UserRole } from '../types/UserRole';
import { MasterCategoryConst, type MasterCategoryType } from '../types/masterSearchTypes';
import { MASTER_REFERENCE_CONFIG } from '../data/masterReferenceConfig';

/**
 * codesの型定義
 */
interface UserCodes {
  ja: number | null;
  seller: number | null;
  farmer: number | null;
  buyer: number | null;
}

export const useUserMasterCodeName = (role: string, initialMasterCode: string) => {
  const { data: jaData } = useMasterReferenceQuery(true, MasterCategoryConst.JA);
  const { data: sellerData } = useMasterReferenceQuery(true, MasterCategoryConst.SELLER);
  const { data: buyerData } = useMasterReferenceQuery(true, MasterCategoryConst.BUYER);
  const { data: farmerData } = useMasterReferenceQuery(true, MasterCategoryConst.FARMER);

  // 初期値をすべて null に設定
  const [codes, setCodes] = useState<UserCodes>({ ja: null, seller: null, farmer: null, buyer: null });
  const [names, setNames] = useState({ ja: '', seller: '', farmer: '', buyer: '' });

  // --- 初期値セットロジック ---
  useEffect(() => {
    // 新規登録時（初期コードが空）の場合
    if (!initialMasterCode) {
      if (role === UserRole.Admin) {
        // 市場の場合は「8888」をデフォルトにする
        setCodes({ ja: null, seller: 8888, farmer: 8888, buyer: null });
      } else {
        setCodes({ ja: null, seller: null, farmer: null, buyer: null });
      }
      return;
    }

    // 既存データ編集時のパース処理
    if (role === UserRole.Farmer) {
      const [s, f] = initialMasterCode.split('_');
      setCodes({ 
        ja: null ,
        seller: s ? Number(s) : null, 
        farmer: f ? Number(f) : null, 
        buyer: null 
      });
    } else if (role === UserRole.Admin) {
      // Adminの場合
      setCodes({ 
        ja: null ,
        seller: 8888, 
        farmer: 8888, 
        buyer: null 
      });
    } else if (role === UserRole.JA) {
      setCodes({ ja: Number(initialMasterCode), seller: null, farmer: null, buyer: null });
    } else if (role === UserRole.Seller) {
      setCodes({ ja: null, seller: Number(initialMasterCode), farmer: null, buyer: null });
    } else if (role === UserRole.Buyer) {
      setCodes({ ja: null, seller: null, farmer: null, buyer: Number(initialMasterCode) });
    }
  }, [initialMasterCode, role]);

  // --- 名称取得ロジック ---
  useEffect(() => {
	// code を number | null で受け取れるように型を調整
    const findName = (data: any, code: number | null, type: MasterCategoryType, currentSellerCode?: number | null) => {
      if (!data?.masters || code === null) return '';
      
      const config = MASTER_REFERENCE_CONFIG[type];
      const targetCode = code;

      if (type === MasterCategoryConst.FARMER) {
        if (currentSellerCode === null || currentSellerCode === undefined) return '';
        
        const sKey = 'sellerCode'; 
        const fKey = config.columns[1].accessor;
        const nKey = config.columns[2].accessor;
        
        const target = data.masters.find((m: any) => 
          Number(m[sKey]) === currentSellerCode && 
          Number(m[fKey]) === targetCode
        );
        return target ? target[nKey] : '';
      } else {
        const codeKey = config.columns[0].accessor;
        const nameKey = config.columns[1].accessor;
        
        const target = data.masters.find((m: any) => Number(m[codeKey]) === targetCode);
        return target ? target[nameKey] : '';
      }
    };

    setNames({
      ja: findName(jaData, codes.ja, MasterCategoryConst.JA),
      seller: findName(sellerData, codes.seller, MasterCategoryConst.SELLER),
      farmer: findName(farmerData, codes.farmer, MasterCategoryConst.FARMER, codes.seller),
      buyer: findName(buyerData, codes.buyer, MasterCategoryConst.BUYER),
    });
  }, [codes, jaData, sellerData, farmerData, buyerData]);

  return { codes, setCodes, names };
};

export const useCodeName = () => {
  const { data: sellerData } = useMasterReferenceQuery(true, MasterCategoryConst.SELLER);
  const { data: farmerData } = useMasterReferenceQuery(true, MasterCategoryConst.FARMER);

  // 初期値をすべて null に設定
  const [codes, setCodes] = useState<UserCodes>({ ja: null, seller: null, farmer: null, buyer: null });
  const [names, setNames] = useState({ seller: '', farmer: ''});

  // --- 名称取得ロジック ---
  useEffect(() => {
	// code を number | null で受け取れるように型を調整
    const findName = (data: any, code: number | null, type: MasterCategoryType, currentSellerCode?: number | null) => {
      if (!data?.masters || code === null) return '';
      
      const config = MASTER_REFERENCE_CONFIG[type];
      const targetCode = code;

      if (type === MasterCategoryConst.FARMER) {
        if (currentSellerCode === null || currentSellerCode === undefined) return '';
        
        const sKey = 'sellerCode'; 
        const fKey = config.columns[1].accessor;
        const nKey = config.columns[2].accessor;
        
        const target = data.masters.find((m: any) => 
          Number(m[sKey]) === currentSellerCode && 
          Number(m[fKey]) === targetCode
        );
        return target ? target[nKey] : '';
      } else {
        const codeKey = config.columns[0].accessor;
        const nameKey = config.columns[1].accessor;
        
        const target = data.masters.find((m: any) => Number(m[codeKey]) === targetCode);
        return target ? target[nameKey] : '';
      }
    };

    setNames({
      seller: findName(sellerData, codes.seller, MasterCategoryConst.SELLER),
      farmer: findName(farmerData, codes.farmer, MasterCategoryConst.FARMER, codes.seller),
    });
  }, [codes, sellerData, farmerData]);

  return { codes, setCodes, names };
};